<template>
  <div class="d-flex">
    <div class="d-flex align-items-center">
      <span v-if="!allTariffs" class="clickable" @click="showModal">
        {{ distributionsList.length  + ' ' + $t('tariff_periods') }} 
      </span>
      <span v-else >
        {{ $t('all_tariffs') | capitalize }}
      </span>
      <div v-if="!allTariffs" class="ml-2 clickable" @click="showModal">
        <fa-icon v-if="editMode" :icon="['fa', 'pen']"/>
        <fa-icon
          v-else-if="!editMode"
          class="blue"
          size="sm"
          :icon="['fas', 'plus-circle']"
        />
      </div>
    </div>
    <b-modal
      id="valuesModal"
      ref="valuesModal"
      modal-class="custom-modal info"
      size="xl"
      centered
      hide-footer>
      <template #modal-title>
        <h4 class="d-flex align-items-center mb-0">
          <fa-icon :icon="['fas', 'exclamation-circle']" class="icon-blue mr-2"/>
          {{ $t('tariffs_in_scope') | capitalize }}
        </h4>
      </template>
      <multi-select 
        v-if="editMode"
        class="mb-3"
        v-model="selectedDistributionIdToAdd" 
        id="tariffPeriodsSearch" 
        label="name" 
        track-by="value" 
        :placeholder="$t('search_tariff_period') | capitalize"
        :options="tariffPeriodSelectOptions" 
        :multiple="false" 
        :searchable="true" 
        :loading="isSelectLoading" 
        :close-on-select="true" 
        :show-no-results="true" 
        @search-change="findTariffOptions"
        >
        <template #option="props">
          <div class="option__desc">
            <span class="option__small">{{ props.option.name }}</span>
            <p class="option__title" v-if="props.option.warning">Warning: Amount balance is bellow moratorium minimum!</p>
          </div>
        </template>
      </multi-select>
      <pagination
        class="smaller p-0"
        :page="page"
        :count="distributionsList.length"
        :loading="isDistributionInfoLoading"
        v-model="limit"
        @limit="getDistributionsInfo"
      />
      <div class="list-container">
        <div v-if="isDistributionInfoLoading" class="loading-container">
          <b-spinner  variant="light" />
        </div>
        <div class="list" v-else>
          <div v-for="id in pagedIds" :key="id" class="list-item">
            <div>
              {{ getDistributionDescription(id) }}
            </div>
            <div>
              <fa-icon :icon="['fas', 'times']" class="text-red clickable" v-if="editMode" @click="removeDistribution(id)"/>
            </div>
          </div>
        </div>
      </div>
      <table-pagination
        v-model="page"
        :total-rows="distributionsList.length"
        :per-page="limit"
        @input="getDistributionsInfo"
      />
      <b-row class="modal-footer float-right">
        <b-button class="btn-outline-red" @click="cancelChanges()">
          {{ $t("cancel") | capitalize }}
        </b-button>
        <b-button class="btn-outline-green" :disabled="!hasChanged" @click="acceptChanges()" v-if="editMode">
          {{ $t("accept") | capitalize }}
        </b-button>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import Pagination from "@/components/Pagination"
import { distributionCollegeTypes } from "@/constants"

export default {
  name: "PaginatedDistributionsModal",
  components: {
    Pagination,
  },
  props: {
    id: {
      type: String,
    },
    distributions: {
      type: Array,
      default: () => [],
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    allTariffs: {
      type: Boolean,
      default: false,
    },
    payoutData: {
      type: Object,
      default: () => { return {} }
    },
    selectedCopyrights: {
      type: Object,
      default: () => { return { values: [], except: false } }
    },
    copyrightsOptions: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      page: 1,
      limit: 10,
      fetchedDistributionsMap: {},
      editableDistributions: [],
      isDistributionInfoLoading: false,
      isSelectLoading: false,
      tariffPeriodSelectOptions: [],
      selectedDistributionIdToAdd: null,
      distributionsIdsOptions: [],
      hasChanged: false,
    }
  },
  methods: {
    async getDistributionsInfo () {
      const data = { distribution_ids: this.missingPagedDistributions }
      if (data.distribution_ids.length === 0) {
        return
      }
      const results = {}
      try {
        this.isDistributionInfoLoading = true
        const resp = await this.$api.payouts.getDistributionMapForPayout(
          this.id,
          data
        )
        Object.assign(results, resp.data)
      } catch (err) {
        console.error("Error fetching distribution map batch:", err)
      } finally {
        this.isDistributionInfoLoading = false
      }
      for (const key in results) {
        const lowerKey = key.toLowerCase()
        if (lowerKey !== key) {
          results[lowerKey] = results[key]
          delete results[key]
        }
      }
      this.fetchedDistributionsMap = { ...this.fetchedDistributionsMap, ...results }
    },
    showModal () {
      this.$refs.valuesModal.show()
    },
    getDistributionDescription (id) {
      return this.fetchedDistributionsMap[id] ? this.fetchedDistributionsMap[id].description : ""
    },
    removeDistribution (id) {
      const index = this.editableDistributions.indexOf(id)
      if (index > -1) {
        this.editableDistributions.splice(index, 1)
        this.getDistributionsInfo()
        this.hasChanged = true
      }
    },
    findTariffOptions (query) {
      if (query) {
        let copyrightFilterForTariffs = []
        if (!this.selectedCopyrights.except) {
          copyrightFilterForTariffs = this.selectedCopyrights.values.map((c) => c.value)
        } else if (this.selectedCopyrights.except) {
          const selectedValues = this.selectedCopyrights.values.map((item) => item.value)
          copyrightFilterForTariffs = this.copyrightsOptions.map(o => o.value).filter((item) => !selectedValues.includes(item))
        }
        this.isSelectLoading = true
        let college = this.payoutData.scope.college
        let college_filter_value = [distributionCollegeTypes.MAKER_AND_PERFORMER]
        let moratorium_filter = {}
        if (college === distributionCollegeTypes.MAKER) {
          college_filter_value.push(distributionCollegeTypes.MAKER)
          moratorium_filter.moratorium_active__maker__eq = false
        } else if (college === distributionCollegeTypes.PERFORMER) {
          college_filter_value.push(distributionCollegeTypes.PERFORMER)
          moratorium_filter.moratorium_active__performer__eq = false
        } else {
          college_filter_value.push(distributionCollegeTypes.MAKER, distributionCollegeTypes.PERFORMER)
          moratorium_filter.moratorium_active__maker__eq = false
          moratorium_filter.moratorium_active__performer__eq = false
        }
        let distributionQuery = {
          "description": query,
          "college": college_filter_value,
          "copyright": copyrightFilterForTariffs,
          ...moratorium_filter,
        }
        this.$api.distributions.distributionsList(distributionQuery).then(result => {
          this.tariffPeriodSelectOptions = result.data.results
            .filter(r => {
              return !this.distributionsList.includes(r.id)
            })
            .map(r => {
              return {
                "name": r["description"],
                "value": r["description"],
                "id": r.id,
                "resoundId": r.resoundId,
                "warning": this.isBelowMoratorium(r),
              }
            })
          this.isSelectLoading = false
        })
      }
    },
    isBelowMoratorium (distribution) {
      if (this.payoutData.scope.college === distributionCollegeTypes.MAKER) {
        return distribution.isMoratoriumAlertMaker
      } else if (this.payoutData.scope.college === distributionCollegeTypes.PERFORMER) {
        return distribution.isMoratoriumAlertPerformer
      } else {
        return distribution.isMoratoriumAlertMaker || distribution.isMoratoriumAlertPerformer
      }
    },
    cancelChanges () {
      this.resetChanges()
      this.emitChanges()
      this.$bvModal.hide(this.$refs.valuesModal.id)
    },
    acceptChanges () {
      this.emitChanges()
      this.emitChanged()
      this.$bvModal.hide(this.$refs.valuesModal.id)
    },
    resetChanges () {
      if (this.editMode) {
        this.editableDistributions = [...this.distributions]
      }
      this.hasChanged = false
    },
    emitChanges () {
      this.$emit("updatedistributions", this.editableDistributions)
    },
    emitChanged () {
      this.$emit("haschanged", this.hasChanged)
    }
  },
  computed: {
    pagedIds () {
      const start = (this.page - 1) * this.limit
      const end = start + this.limit
      return this.distributionsList.slice(start, end)
    },
    missingPagedDistributions () {
      return this.pagedIds.filter(id => !this.fetchedDistributionsMap[id])
    },
    distributionsList () {
      return this.editMode ? this.editableDistributions : this.distributions
    }
  },
  mounted () {
    this.resetChanges()
    this.getDistributionsInfo()
    if (!this.allTariffs && this.editMode) {
      this.emitChanges()
    }
  },
  watch: {
    selectedDistributionIdToAdd (newVal) {
      if (newVal) {
        if (newVal.id && !this.distributionsList.includes(newVal.id)) {
          this.editableDistributions.unshift(newVal.id)
          this.getDistributionsInfo()
        }
        this.selectedDistributionIdToAdd = null
        this.hasChanged = true
      }
    },
    allTariffs (value) {
      if (!value) {
        this.emitChanges()
      }
    },
    editMode (value) {
      if (value) {
        this.resetChanges()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
}

.list-container {
  height: 426px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.list {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.list-item {
  padding: 10px 0;
  display: flex;
  justify-content: space-between;
}

.list-item:not(:last-child) {
  border-bottom: 1px solid #e0e0e0;
}
</style>